import React, { useState, useCallback, useEffect } from "react";

import AnnotationCanvas from "./AnnotationCanvas";
import { useAnnotationNavigation } from "../hooks/useAnnotationNavigation";
import { ImageNavigation } from "../toolbar/ImageNavigation";

/**
 * Media item representation for annotation editor
 */
export interface MediaItem {
  key: string;
  url?: string;
  extension?: string;
  data?: string;
}

/**
 * Props for the AnnotationEditor component
 */
export interface AnnotationEditorProps {
  /** Collection of media items to annotate */
  media: MediaItem[];

  /** Currently active media index */
  currentIndex: number;

  /** Navigation callback for media switching */
  onNavigate: (index: number) => void;

  /** Optional callback for active media change notifications */
  onActiveMediaChange?: (mediaSetId: string, mediaKey: string) => void;

  /** Media set identifier for annotation grouping */
  mediaSetId: string;
}

/**
 * AnnotationEditor - Complete annotation editor with multi-image support
 *
 * This component:
 * - Accepts all data via props (dependency injection)
 * - Manages annotation mode state internally
 * - Coordinates between AnnotationCanvas and ImageNavigation
 * - Notifies parent of active media changes via callback
 */
const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  media,
  currentIndex,
  onNavigate,
  onActiveMediaChange,
  mediaSetId,
}) => {
  const images = media.map((m) => m.url);

  /**
   * Internal annotation mode state management
   */
  const [isAnnotating, setIsAnnotating] = useState(false);

  /**
   * Navigation handler for media traversal
   */
  const handleNavigate = useCallback(
    (newIndex: number) => {
      onNavigate(newIndex);
    },
    [onNavigate],
  );

  /**
   * Toggle annotation mode on/off
   */
  const handleToggleAnnotating = useCallback(() => {
    setIsAnnotating(!isAnnotating);
  }, [isAnnotating]);

  const currentImageUrl = images[currentIndex];
  const currentMediaKey = media[currentIndex]?.key;

  /**
   * Notify parent component of active media changes for state synchronization
   */
  useEffect(() => {
    if (currentMediaKey && onActiveMediaChange) {
      onActiveMediaChange(mediaSetId, currentMediaKey);
    }
  }, [mediaSetId, currentMediaKey, onActiveMediaChange]);

  /**
   * Enable keyboard navigation when not in annotation mode
   */
  useAnnotationNavigation({
    enabled: !isAnnotating,
    currentIndex,
    totalImages: images.length,
    onNavigate: handleNavigate,
  });

  return (
    <div className="group/image-viewer relative h-full w-full">
      <AnnotationCanvas
        imageUrl={currentImageUrl}
        isAnnotating={isAnnotating}
        mediaKey={currentMediaKey}
        mediaSetId={mediaSetId}
        onToggleAnnotating={handleToggleAnnotating}
      />

      {/* Conditional navigation controls for multi-image workflows */}
      {!isAnnotating && images.length > 1 && (
        <ImageNavigation currentIndex={currentIndex} totalImages={images.length} onNavigate={handleNavigate} />
      )}
    </div>
  );
};

export default AnnotationEditor;
